import {
	addLoFConfig,
	getRiskConfig,
	saveLoF,
	setLoFConfig,
	setLofIsChanged,
	setRefreshList,
	setTab,
} from '@Actions';
import { Category as CategoryComponent } from '@Components/Category/Category.component';
import Scales from '@Components/Utils/icons/Scales';
import { NavigationPrompt } from '@innovyze/stylovyze';
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	TextField,
	Typography,
} from '@mui/material';
import { selectLofStore, selectRiskSetting } from '@Selectors';
import { ComponentProps } from '@Types';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useGlobalization } from '@Translations/useGlobalization';

export const LoFSetup = ({
	onAddComponent,
	onEditComponent,
}: ComponentProps) => {
	const { t } = useGlobalization();
	const [open, setOpen] = React.useState(false);
	const [newCategoryName, setNewCategoryName] = useState('');
	const [validCategoryName, setValidCategoryName] = useState<string>('');
	const dispatch = useDispatch();
	const history = useHistory();

	const riskSettings = selectRiskSetting();
	const lofStore = selectLofStore();
	const categories = lofStore?.categories;
	const isLoading =
		lofStore?.isLoadingCategories ||
		lofStore?.isLoadingFields ||
		lofStore?.isLoadingInspectionFields;

	useEffect(() => {
		dispatch(setTab('lof'));
	}, []);

	const handleClose = () => {
		setNewCategoryName('');
		setValidCategoryName('');
		setOpen(false);
	};

	const handleAddCategoryClicked = () => {
		if (!isLoading) setOpen(true);
	};

	const handleAdd = () => {
		if (newCategoryName !== '') {
			dispatch(
				addLoFConfig({
					name: newCategoryName,
					isCondition: false,
					useThisOnly: false,
					weighting: 0,
					useHighestScore: false,
					components: [],
				}),
			);
			setOpen(false);
			setNewCategoryName('');
			setValidCategoryName('');
		}
	};

	const totalWeight = lofStore?.categories
		?.filter(cat => !cat.useThisOnly)
		?.map(cat => cat.weighting)
		?.reduce((pre, current) => pre + current, 0);

	const validateCategoryName = (event: ChangeEvent<HTMLInputElement>) => {
		const name = event.target.value.toUpperCase();
		let valid = true;
		valid =
			lofStore?.categories.find(
				category => category.name.toUpperCase() == name,
			) === undefined;
		setValidCategoryName(valid ? '' : t('Enter a unique name'));
		setNewCategoryName(valid ? event.target.value : '');
	};

	const isConfigValid = () => {
		const categories = lofStore.categories;
		if (!categories) return false;
		if (categories.length === 0) {
			return false;
		}
		const categoriesTotalWeight = categories
			.filter(category => !category.useThisOnly)
			.map(category => category.weighting)
			.reduce((prev, current) => prev + current, 0);
		if (
			categoriesTotalWeight !== 100 &&
			categories.filter(category => !category.useThisOnly).length > 0
		)
			return false;
		for (const category of categories) {
			if (
				!category.useHighestScore &&
				category.components &&
				category.components.length != 0
			) {
				const total = category.components
					.map(component =>
						component.select ? component.weighting : 0,
					)
					.reduce((prev, current) => prev + current, 0);

				// We want a total of 0 or 100 to be valid
				return total === 0 || total === 100;
			}
		}
		return true;
	};

	const noLoFCategories = () => {
		return (
			<Grid container>
				<Grid
					container
					item
					xs={12}
					justifyContent="center"
					style={{ paddingTop: '40px' }}>
					<Scales />
				</Grid>
				<Grid container item xs={12} justifyContent="center">
					<Typography variant="h6">
						{t('No LoF Categories')}
					</Typography>
				</Grid>
				<Grid container item xs={12} justifyContent="center">
					<Typography
						variant="caption"
						style={{ textAlign: 'center' }}>
						{t('You have no LoF categories created.')}
					</Typography>
				</Grid>
				<Grid
					container
					item
					xs={12}
					justifyContent="center"
					style={{
						paddingTop: '20px',
						paddingBottom: '20px',
					}}>
					<Button
						variant="contained"
						color="primary"
						onClick={handleAddCategoryClicked}>
						{t('Add Category')}
					</Button>
				</Grid>
			</Grid>
		);
	};

	const navMsg = isConfigValid()
		? t(
				'You have modified your LOF configuration and have unsaved changes.  You can save your configuration changes, discard changes, or cancel to continue editing.',
		  )
		: t(
				'You have modified your LOF configuration and have unsaved changes.  You can discard changes, or cancel to continue editing.',
		  );

	const refresh = () => {
		dispatch(getRiskConfig(riskSettings.configId));
	};

	return (
		<>
			<NavigationPrompt
				when={lofStore.isChanged}
				navigate={nextLocation => {
					history.push(nextLocation);
				}}
				confirmText={isConfigValid() ? t('Save') : t('Discard')}
				onNavigateAway={() => {
					if (isConfigValid()) {
						dispatch(saveLoF({ refresh: refresh }));
						dispatch(setRefreshList(true));
					} else {
						dispatch(getRiskConfig(riskSettings.configId));
						dispatch(setLofIsChanged(false));
					}
				}}
				secondaryConfirmText={
					isConfigValid() ? t('Discard') : undefined
				}
				onSecondaryNavigateAway={() => {
					dispatch(getRiskConfig(riskSettings.configId));
					dispatch(setLofIsChanged(false));
				}}
				shouldBlockNavigation={() => true}
				shouldBlockReload={lofStore.isChanged}
				content={navMsg}></NavigationPrompt>
			<Grid container>
				<Grid xs={12} item container>
					{riskSettings.assetType !== 'pipe' ? (
						<Grid item xs={12}>
							{!isLoading && categories.length > 0 ? (
								<CategoryComponent
									isLoading={false}
									forLoF={true}
									LoFCategoryIndex={0}
									isWeightingInvalid={totalWeight != 100}
									onCategoryWeightingChanged={(
										value: number,
									) => {
										dispatch(
											setLoFConfig(
												categories.map((cat, index) =>
													index === 0
														? {
																...cat,
																weighting:
																	value,
														  }
														: cat,
												),
											),
										);
									}}
									onAddComponent={(forLoF, targetCat) => {
										if (targetCat)
											onAddComponent(
												true,
												true,
												targetCat,
											);
									}}
									onEditComponent={(
										forLoF,
										targetCat,
										component,
									) => {
										onEditComponent(
											true,
											true,
											targetCat,
											component,
										);
									}}
								/>
							) : (
								<CategoryComponent
									isLoading={true}
									forLoF={true}
									LoFCategoryIndex={0}
									isWeightingInvalid={false}
								/>
							)}
						</Grid>
					) : (
						[]
					)}
					{isLoading ? null : (
						<Grid item xs={12} container justifyContent="flex-end">
							<Button
								style={{ margin: '12px 0px' }}
								variant="contained"
								color="primary"
								onClick={handleAddCategoryClicked}>
								{t('Add Category')}
							</Button>
						</Grid>
					)}
					{riskSettings.assetType === 'pipe' &&
					categories.length <= 1 ? (
						isLoading ? (
							<Grid
								container
								item
								xs={12}
								justifyContent="center"
								style={{
									paddingTop: '20px',
									paddingBottom: '20px',
								}}>
								<CircularProgress />
							</Grid>
						) : (
							noLoFCategories()
						)
					) : (
						categories
							.filter((cat, index) => index > 0)
							.map((cat, index) => (
								<Grid
									item
									xs={12}
									key={cat.name}
									style={{ paddingBottom: '12px' }}>
									<CategoryComponent
										isLoading={false}
										forLoF={true}
										LoFCategoryIndex={index + 1}
										isWeightingInvalid={totalWeight != 100}
										onCategoryWeightingChanged={(
											value: number,
										) => {
											dispatch(
												setLoFConfig(
													categories.map(
														(
															onChangeCat,
															onChangeIndex,
														) =>
															onChangeIndex ===
															index + 1
																? {
																		...onChangeCat,
																		weighting:
																			value,
																  }
																: onChangeCat,
													),
												),
											);
										}}
										onAddComponent={(forLoF, targetCat) => {
											onAddComponent(
												true,
												false,
												targetCat,
											);
										}}
										onEditComponent={(
											forLoF,
											targetCat,
											component,
										) => {
											onEditComponent(
												true,
												false,
												targetCat,
												component,
											);
										}}
									/>
								</Grid>
							))
					)}
				</Grid>
				<Grid xs={4} item container>
					{/* <FieldInspectionSummary /> */}
				</Grid>
			</Grid>
			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby="form-dialog-title">
				<DialogTitle id="form-dialog-title" style={{ width: '481px' }}>
					{t('Add Category')}
				</DialogTitle>
				<DialogContent>
					<TextField
						margin="dense"
						id="name"
						label={t('Category Name')}
						type="text"
						fullWidth
						variant="outlined"
						defaultValue={newCategoryName}
						helperText={validCategoryName}
						error={validCategoryName !== ''}
						onChange={validateCategoryName}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleClose}
						color="secondary"
						variant="contained">
						{t('Cancel')}
					</Button>
					<Button
						onClick={handleAdd}
						color="primary"
						variant="contained">
						{t('Add Category')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
